import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { lookupsJson } from '@ourbranch/lookups';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import useStyles from 'common/components/people/add-form.styles';
import { useLocalStyles } from './add-incident-form.styles';
import { getIncidentTypeLabel } from './helpers';

// PUA/PUN are diverted out of rating (see rater-helpers addAutoViolation) and never
// render on this tab, so they are not offered here.
const AUTO_VIOLATION_CLASSES = Object.keys(lookupsJson.autoViolationClassDescriptions).filter(
  (violationClass) => !['PUA', 'PUN'].includes(violationClass)
);

// Verisk A-Plus property loss-type codes (same UI subset as the offer add-home-claim tool);
// the backend validates against the full mapping in @ourbranch/rater-lookups.
const LOSS_TYPE_CODES = [
  'FIRE',
  'WATER',
  'THEFT',
  'DOG',
  'WIND',
  'HAIL',
  'LIGHT',
  'LIABILITY',
  'VMM',
  'WBKP',
  'SMOKE',
  'MOLD',
  'OTHER'
];

// monarch violation classes ratable on home/condo policies (backend validates against monarchPoints)
const HOME_VIOLATION_CLASSES = ['AAF', 'NAF', 'CMP'];

const INCIDENT_SOURCES = [
  { id: 'MVR', value: 'MVR' },
  { id: 'CLAIMS', value: 'CLAIMS' }
];

const HOME_INCIDENT_KINDS = [
  { id: 'claim', value: 'Claim' },
  { id: 'violation', value: 'Violation' }
];

const autoValidationSchema = Yup.object().shape({
  driverId: Yup.string().required('Driver is required'),
  date: Yup.date().max(new Date(), 'Incident date cannot be in the future').required('Date is required'),
  violationClass: Yup.string().required('Incident type is required'),
  source: Yup.string().required('Source is required')
});

const homeValidationSchema = Yup.object().shape({
  kind: Yup.string().required(),
  date: Yup.date().max(new Date(), 'Incident date cannot be in the future').required('Date is required'),
  lossTypeCode: Yup.string().when('kind', {
    is: 'claim',
    then: Yup.string().required('Loss type is required'),
    otherwise: Yup.string().nullable()
  }),
  amount: Yup.number().when('kind', {
    is: 'claim',
    then: Yup.number().min(0, 'Amount must be positive').required('Amount is required'),
    otherwise: Yup.number().nullable()
  }),
  violationClass: Yup.string().when('kind', {
    is: 'violation',
    then: Yup.string().required('Violation type is required'),
    otherwise: Yup.string().nullable()
  })
});

const AddIncidentForm = ({ isAutoPolicy, drivers, onSubmit, loading }) => {
  const classes = useStyles();
  const localClasses = useLocalStyles();

  const driverOptions = useMemo(
    () => drivers.map((driver) => ({ id: driver.id, value: `${driver.firstName} ${driver.lastName}` })),
    [drivers]
  );

  const autoViolationOptions = useMemo(
    () =>
      AUTO_VIOLATION_CLASSES.map((violationClass) => ({
        id: violationClass,
        value: `${violationClass} - ${lookupsJson.autoViolationClassDescriptions[violationClass]}`
      })),
    []
  );

  const handleSubmit = (values, { resetForm }) => {
    if (isAutoPolicy) {
      onSubmit({
        autoViolationsToAdd: [
          {
            driverId: values.driverId,
            date: values.date,
            violationClass: values.violationClass,
            source: values.source,
            isCourtRecord: !!values.isCourtRecord
          }
        ]
      });
    } else if (values.kind === 'claim') {
      onSubmit({
        homeClaimsToAdd: [
          {
            date: values.date,
            lossTypeCode: values.lossTypeCode,
            amount: Number(values.amount),
            actOfGod: !!values.actOfGod,
            wHL: !!values.wHL
          }
        ]
      });
    } else {
      onSubmit({ homeViolationsToAdd: [{ date: values.date, violationClass: values.violationClass }] });
    }
    resetForm();
  };

  const initialValues = isAutoPolicy
    ? { driverId: '', date: '', violationClass: '', source: '', isCourtRecord: false }
    : { kind: 'claim', date: '', lossTypeCode: '', amount: '', actOfGod: false, wHL: false, violationClass: '' };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isAutoPolicy ? autoValidationSchema : homeValidationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form className={classes.container}>
          <Label type="formTitle" className={localClasses.title}>
            Add Incident
          </Label>
          <Label type="infoSubtitleSmall" className={localClasses.scopeNote}>
            Re-rates the policy as if the incident had been present since the start of the term.
          </Label>
          <Grid container spacing={4}>
            {isAutoPolicy ? (
              <>
                <FormField type="select" name="driverId" label="Driver" options={driverOptions} mode="light" xs={6} />
                <FormField type="date" name="date" label="Date of Incident" defaultToEmpty mode="light" xs={6} />
                <FormField
                  type="select"
                  name="violationClass"
                  label="Incident Type"
                  options={autoViolationOptions}
                  mode="light"
                  xs={6}
                />
                <FormField type="select" name="source" label="Source" options={INCIDENT_SOURCES} mode="light" xs={6} />
                <FormField type="checkbox" name="isCourtRecord" label="Court record" mode="light" xs={12} />
              </>
            ) : (
              <>
                <FormField
                  type="select"
                  name="kind"
                  label="Incident Kind"
                  options={HOME_INCIDENT_KINDS}
                  mode="light"
                  xs={6}
                />
                <FormField type="date" name="date" label="Date of Loss" defaultToEmpty mode="light" xs={6} />
                {values.kind === 'claim' ? (
                  <>
                    <FormField
                      type="select"
                      name="lossTypeCode"
                      label="Loss Type"
                      options={LOSS_TYPE_CODES.map((code) => ({ id: code, value: code }))}
                      mode="light"
                      xs={6}
                    />
                    <FormField
                      type="numeric"
                      name="amount"
                      label="Amount Paid"
                      format={{ thousandSeparator: true, prefix: '$', decimalScale: 2 }}
                      mode="light"
                      xs={6}
                    />
                    <FormField type="checkbox" name="actOfGod" label="Act of God" mode="light" xs={6} />
                    <FormField type="checkbox" name="wHL" label="Wind / Hail / Lightning" mode="light" xs={6} />
                  </>
                ) : (
                  <FormField
                    type="select"
                    name="violationClass"
                    label="Violation Type"
                    options={HOME_VIOLATION_CLASSES.map((violationClass) => ({
                      id: violationClass,
                      value: `${violationClass} - ${getIncidentTypeLabel(violationClass)}`
                    }))}
                    mode="light"
                    xs={6}
                  />
                )}
              </>
            )}
          </Grid>
          <div className={classes.buttonRow}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              className={classes.submitButton}
              disabled={loading}
            >
              Add Incident
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

AddIncidentForm.propTypes = {
  isAutoPolicy: PropTypes.bool.isRequired,
  drivers: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

AddIncidentForm.defaultProps = {
  drivers: [],
  loading: false
};

// FormField type="date" renders @material-ui/pickers, which requires MuiPickersUtilsProvider
export default withDatePicker(AddIncidentForm);
